import React, { useState, useMemo, useRef, useEffect } from 'react';

// ux
import LoadingAnimation from '../ux/LoadingAnimation.js';
import FuseListItem from '../ux/FuseListItem.js';

const Fusions = ({
  fuses,
  fetchFuse,
  dashboardLoading,
  handleOpenModal,
  handleViewChange,
  renameFuse
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('date');
  const [selectedFusions, setSelectedFusions] = useState([]);

  const selectAllRef = useRef();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const handleFuseSelect = (fuseId) => {
    setSelectedFusions((prevSelected) => {
      if (prevSelected.includes(fuseId)) {
        // If already selected, remove it
        return prevSelected.filter((id) => id !== fuseId);
      } else {
        // If not selected, add it
        return [...prevSelected, fuseId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedFusions.length === sortedFilteredFusions.length) {
      // If all are selected, deselect all
      setSelectedFusions([]);
    } else {
      // If not all are selected, select all
      const allFuseIds = sortedFilteredFusions.map((fuse) => fuse.ID);
      setSelectedFusions(allFuseIds);
    }
  };

  const sortedFilteredFusions = useMemo(() => {
    // Step 4.1: Filter fuses based on the search term
    let filtered = fuses;
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = fuses.filter(
        (fuse) =>
          (fuse.fuse_title &&
            fuse.fuse_title.toLowerCase().includes(lowerSearchTerm)) ||
          (fuse.brand_name &&
            fuse.brand_name.toLowerCase().includes(lowerSearchTerm))
      );
    }

    // Step 4.2: Sort the filtered fuses based on the selected sort option
    let sorted = [...filtered]; // Create a shallow copy to avoid mutating the original array

    if (sortOption === 'date') {
      // Assuming 'date' is a valid date string or Date object in your fuse object
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date)); // Ascending order (oldest first)
    } else if (sortOption === 'name') {
      sorted.sort((a, b) => {
        const aName = a.fuse_title || a.brand_name || '';
        const bName = b.fuse_title || b.brand_name || '';
        return aName.localeCompare(bName);
      });
    }

    return sorted;
  }, [searchTerm, sortOption, fuses]);

  useEffect(() => {
    if (
      selectedFusions.length > 0 &&
      selectedFusions.length < sortedFilteredFusions.length
    ) {
      selectAllRef.current.indeterminate = true;
    } else {
      selectAllRef.current.indeterminate = false;
    }
  }, [selectedFusions, sortedFilteredFusions.length]);

  return (
    <div className="fuselight-dashboard-container">
      <div className="dashboard-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div className="search-and-sort-container">
        <div className="sort-container">
          <label htmlFor="sort-select" className="sort-label">
            Sort By:
          </label>
          <select
            id="sort-select"
            value={sortOption}
            onChange={handleSortChange}
            className="sort-select"
            aria-label="Sort Fusions"
          >
            <option value="date">Recent</option>
            <option value="name">Title</option>
          </select>
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
            aria-label="Search Fusions"
          />
        </div>
        <button
          className="delete-button"
          onClick={() => handleOpenModal('deleteFusions', selectedFusions)}
          disabled={selectedFusions.length === 0}
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
      <h3>All Fusions</h3>
      <div className="fuselight-dashboard-header">
        <div className="fuselight-dashboard-header-checkbox">
          <label className="custom-checkbox">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={
                selectedFusions.length === sortedFilteredFusions.length &&
                sortedFilteredFusions.length > 0
              }
              onChange={handleSelectAll}
              aria-label="Select All Fusions"
            />
            <span></span>
          </label>
        </div>
        <h4>Title</h4>
        <h4 className="mobile-hide">Fusion</h4>
        <h4>Ignition Thrust</h4>
        <h4>Date</h4>
      </div>
      <div className="fuselight-dashboard-content">
        {dashboardLoading ? (
          <LoadingAnimation label="Loading Fusions" />
        ) : (
          <>
            {sortedFilteredFusions.length > 0 ? (
              <div className="fuselight-dashboard-list">
                {sortedFilteredFusions.map((fuse) => (
                  <FuseListItem
                    key={fuse.ID}
                    fuse={fuse}
                    handleOpenModal={handleOpenModal}
                    fetchFuse={fetchFuse}
                    isSelected={selectedFusions.includes(fuse.ID)}
                    onSelectChange={() => handleFuseSelect(fuse.ID)}
                    renameFuse={renameFuse}
                  />
                ))}
              </div>
            ) : (
              <div className="dashboard-none-found">
                <p>No fusions found</p>
                <button
                  className="btn btn--secondary-outline"
                  onClick={() => handleViewChange('launchpad')}
                >
                  Create One
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Fusions;
